// GiftInformationForm.tsx
import React, { useState, useEffect, useRef } from 'react';

import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './StepStyles.scss';
import '../style.scss';
import '../MobileButtonFix.scss';
import CustomSelect from '../../../common/CustomSelect/CustomSelect.tsx';
import SearchableSelect from '../../../common/SearchableSelect/SearchableSelect.tsx';
import {
  Button,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FiPlus, FiInfo } from "../../../../icons";
import InfoIcon from "../../../../images/info-icon.svg";
import calendarIcon from "../../../../images/calendar-icon.svg";
import ProfileIcon from "../../../../images/profile-icon.png";
import MailIcon from "../../../../images/email-icon.png";
import CallIcon from "../../../../images/call-icon.png";
import AddressIcon from "../../../../images/location-icon.png";
import EditIcon from "../../../../images/edit-icon.png";
import VerifiedIcon from "../../../../images/icon_verified.png";
import DatePicker from "react-multi-date-picker";
import type { DateObject } from "react-multi-date-picker";
import apiConfig from '../../config/apiConfig.js';
import { locationApi } from '../../Auth/utils/LocationApi.tsx';
import { 
  createPhoneChangeHandler, 
  handlePhoneKeyDown, 
  getRawPhoneDigits,
  enhancedSanitizers 
} from '../../Auth/utils/phoneFormatter.js';
import { useGiftContext } from '../GiftContext.tsx';
import { 
  Contact, 
  State, 
  City, 
  GiftFormData, 
  RecipientFormData, 
  FormErrors, 
  TouchedFields,
  CreateGiftRequest
} from '../GiftFlow.ts';

interface GiftInformationFormProps {
  onNext?: () => void;
}

// Animation variants for consistent animations
const buttonHover = {
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
  transition: { duration: 0.2 }
};

const subtleButtonHover = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.98 },
  transition: { duration: 0.2 }
};


const GiftInformationForm: React.FC<GiftInformationFormProps> = ({ onNext }) => {
  const { currentGift, createGift, saveGiftAsDraft, updateGiftData, isLoading, markStepCompleted, loadGift } = useGiftContext();
  
  const [startDate, setStartDate] = useState<Date | null>(null);
  const datePickerRef = useRef<any>(null);
  const estimatedDatePickerRef = useRef<any>(null);
  const previousGiftTypeRef = useRef<string>('Regular');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [occasionType, setOccasionType] = useState<'Fixed' | 'Conditional'>('Fixed');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  
  // New states for functionality
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<Contact | null>(null);
  const [giftType, setGiftType] = useState<'Regular' | 'Post-Mortem'>('Regular');
  const [deliverySchedule, setDeliverySchedule] = useState<'One-Time' | 'Recurring'>('One-Time');
  
  // Track if radio fields have been changed (to decide which API to call)
  const [hasRadioChanges, setHasRadioChanges] = useState<boolean>(false);
  
  // Primary Legacy Contact state
  const [primaryLegacyContact, setPrimaryLegacyContact] = useState<Contact | null>(null);

  // User profile SSN state
  const [userHasEnteredSSN, setUserHasEnteredSSN] = useState<boolean>(false);
  const [userProfileSSN, setUserProfileSSN] = useState<string>('');

  // States for location dropdowns
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [loadingCities, setLoadingCities] = useState<boolean>(false);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  
  // Form data state
  const [formData, setFormData] = useState<GiftFormData>({
    title: '',
    recipientId: '',
    occasion: '',
    milestone: '',
    frequency: '',
    numberOfYears: '', // Added numberOfYears field
    additionalComments: '',
    ssn: '',
    ssnConfirmation: ''
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  
  // Recipient data state
  const [recipientData, setRecipientData] = useState<RecipientFormData>({
    name: '',
    relationship: '',
    address1: '',
    address2: '',
    state: '',
    city: '',
    zip: '',
    email: '',
    phone: '+1',
    altPhone: '+1',
  });
  
  // Recipient form validation errors
  const [recipientErrors, setRecipientErrors] = useState<FormErrors>({});
  const [recipientTouched, setRecipientTouched] = useState<TouchedFields>({});

  // Fetch user profile to check if SSN already entered
  const fetchUserProfile = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/profile`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (response.data) {
        const hasSSN = response.data.hasEnteredSSN || false;
        const ssn = response.data.ssn || '';

        setUserHasEnteredSSN(hasSSN);
        setUserProfileSSN(ssn);

      }
    } catch (error) {

    }
  };

  // Fetch contacts and states on component mount
  useEffect(() => {
    fetchContacts();
    fetchStates();
    fetchUserProfile();
  }, []);

  // Update selected recipient when selection changes
  useEffect(() => {
    if (formData.recipientId && contacts.length > 0) {
      const recipient = contacts.find(contact => contact._id === formData.recipientId);
      setSelectedRecipient(recipient || null);
    }
  }, [formData.recipientId, contacts]);

  // Fetch primary legacy contact when Post-Mortem gift type is selected
  useEffect(() => {
    if (giftType === "Post-Mortem" && contacts.length > 0) {
      const primaryLegacy = contacts.find(contact => contact.isPrimaryLegacyContact);
      setPrimaryLegacyContact(primaryLegacy || null);
      
      // Trigger location resolution for the primary legacy contact
      if (primaryLegacy) {
        getLocationDisplayNames(primaryLegacy);
      }
    } else {
      setPrimaryLegacyContact(null);
    }
  }, [giftType, contacts, states]); // Added states dependency

  // State to store resolved location names for contacts
  const [resolvedLocations, setResolvedLocations] = useState<{[key: string]: {cityName: string, stateName: string}}>({});

  // Helper function to get display names for city and state
  const getLocationDisplayNames = async (contact: Contact): Promise<{cityName: string, stateName: string}> => {
    if (!contact) return { cityName: '', stateName: '' };
    
    const contactKey = `${contact._id}_${contact.city}_${contact.state}`;
    
    // Return cached result if available
    if (resolvedLocations[contactKey]) {
      return resolvedLocations[contactKey];
    }
    
    // If city and state are already names (strings), return them
    if (typeof contact.city === 'string' && typeof contact.state === 'string') {
      // Check if they look like ObjectIds (24-character hex strings)
      const isObjectId = (str: string) => /^[0-9a-fA-F]{24}$/.test(str);
      
      if (isObjectId(contact.city) || isObjectId(contact.state)) {
        try {
          // Resolve state name
          let stateName = contact.state;
          const stateObj = states.find(s => s._id === contact.state);
          if (stateObj) {
            stateName = stateObj.name;
          }
          
          // Resolve city name by fetching cities for this state
          let cityName = contact.city;
          if (isObjectId(contact.city) && contact.state) {
            try {
              const citiesResponse = await locationApi.getCitiesByStateId(contact.state);
              if (citiesResponse.status === 'Success' && citiesResponse.cities) {
                const cityObj = citiesResponse.cities.find(c => c._id === contact.city);
                if (cityObj) {
                  cityName = cityObj.name;
                }
              }
            } catch (error) {

            }
          }
          
          const result = { cityName, stateName };
          
          // Cache the result
          setResolvedLocations(prev => ({
            ...prev,
            [contactKey]: result
          }));
          
          return result;
        } catch (error) {

          return {
            cityName: contact.city,
            stateName: contact.state
          };
        }
      } else {
        // They are already display names
        const result = {
          cityName: contact.city,
          stateName: contact.state
        };
        
        // Cache the result
        setResolvedLocations(prev => ({
          ...prev,
          [contactKey]: result
        }));
        
        return result;
      }
    }
    
    return {
      cityName: contact.city || '',
      stateName: contact.state || ''
    };
  };

  // Synchronous version that uses cached data
  const getLocationDisplayNamesSync = (contact: Contact): {cityName: string, stateName: string} => {
    if (!contact) return { cityName: '', stateName: '' };
    
    const contactKey = `${contact._id}_${contact.city}_${contact.state}`;
    
    // Return cached result if available
    if (resolvedLocations[contactKey]) {
      return resolvedLocations[contactKey];
    }
    
    // If not cached, return original values and trigger async resolution
    if (typeof contact.city === 'string' && typeof contact.state === 'string') {
      const isObjectId = (str: string) => /^[0-9a-fA-F]{24}$/.test(str);
      
      if (isObjectId(contact.city) || isObjectId(contact.state)) {
        // Trigger async resolution
        getLocationDisplayNames(contact);
        
        // Return fallback for now
        const stateObj = states.find(s => s._id === contact.state);
        return {
          cityName: contact.city, // Will show ObjectId until resolved
          stateName: stateObj?.name || contact.state
        };
      }
    }
    
    return {
      cityName: contact.city || '',
      stateName: contact.state || ''
    };
  };

  // Load existing gift data if available
  useEffect(() => {
    if (currentGift) {

      setFormData({
        // Clear "Untitled Gift" placeholder so user must provide a real title
        title: currentGift.title === 'Untitled Gift' ? '' : (currentGift.title || ''),
        recipientId: currentGift.recipientId || '',
        occasion: currentGift.occasion || '',
        milestone: currentGift.milestone || '',
        frequency: currentGift.frequency || '',
        numberOfYears: currentGift.numberOfYears ? String(currentGift.numberOfYears) : '',
        additionalComments: currentGift.additionalComments || '',
        ssn: currentGift.ssn ? maskSSN(currentGift.ssn) : '',
        ssnConfirmation: currentGift.ssn ? maskSSN(currentGift.ssn) : ''
      });

      // Update SSN digits if loading existing gift
      if (currentGift.ssn) {
        const cleanSSN = currentGift.ssn.replace(/\D/g, '');
        setSsnDigits(cleanSSN);
        setSsnConfirmationDigits(cleanSSN);
      }

      if (currentGift.type) {
        setGiftType(currentGift.type);
        // Sync the ref immediately to prevent the date-clearing effect from triggering
        previousGiftTypeRef.current = currentGift.type;
      }
      if (currentGift.occasionType) setOccasionType(currentGift.occasionType);
      if (currentGift.deliverySchedule) setDeliverySchedule(currentGift.deliverySchedule);

      // Load the appropriate date based on occasion type
      if (currentGift.occasionType === 'Fixed' && currentGift.date) {
        setStartDate(new Date(currentGift.date));
      } else if (currentGift.occasionType === 'Conditional' && currentGift.milestoneEstimateDate) {
        setStartDate(new Date(currentGift.milestoneEstimateDate));
      } else if (!currentGift.occasionType && currentGift.date) {
        // Default case for backwards compatibility
        setStartDate(new Date(currentGift.date));
      }

    }
  }, [currentGift]);


  // Removed automatic date initialization - let users select their own date

  // Auto-set delivery schedule to "One-Time" when occasion type is "Conditional"
  useEffect(() => {
    if (occasionType === "Conditional") {
      setDeliverySchedule("One-Time");
    }
  }, [occasionType]);

  // Auto-set occasion type to "Fixed" when gift type is "Post-Mortem"
  useEffect(() => {
    if (giftType === "Post-Mortem" && occasionType !== "Fixed") {
      setOccasionType("Fixed");
      setHasRadioChanges(true);
    }
  }, [giftType]);

  // Track gift type changes for ref updates (date clearing moved to radio button handlers)
  useEffect(() => {
    // Update the ref for tracking - date clearing is now handled in onChange handlers
    previousGiftTypeRef.current = giftType;
  }, [giftType]);

  // Function to update radio fields AND their related input fields when editing from draft
  const updateRadioField = async (fieldName: 'type' | 'occasionType' | 'deliverySchedule', fieldValue: string) => {
    // ONLY call update API if we're editing an existing gift (has _id)
    if (!currentGift?._id) {

      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const updatePayload: any = { [fieldName]: fieldValue };

      // Include related input fields based on the radio selection
      if (fieldName === 'type') {
        if (fieldValue === 'Post-Mortem') {
          // Include SSN field for Post-Mortem gifts - use actual SSN value
          updatePayload.ssn = ssnDigits || currentGift?.ssn || '';

          // Include Primary Legacy Contact Information for Post-Mortem gifts
          if (primaryLegacyContact) {
            const { cityName, stateName } = await getLocationDisplayNames(primaryLegacyContact);
            
            updatePayload.primaryLegacyContact = {
              name: primaryLegacyContact.name,
              email: primaryLegacyContact.email,
              phoneNumber: primaryLegacyContact.phoneNumber,
              alternatePhoneNumber: primaryLegacyContact.alternatePhoneNumber || '',
              relationship: primaryLegacyContact.relationship,
              address1: primaryLegacyContact.address1 || '',
              address2: primaryLegacyContact.address2 || '',
              city: cityName || '',
              state: stateName || '',
              zip: primaryLegacyContact.zip || ''
            };
          }
        }
      } else if (fieldName === 'occasionType') {
        if (fieldValue === 'Fixed') {
          // Include occasion and date fields for Fixed occasions
          updatePayload.occasion = formData.occasion || currentGift.occasion || '';
          if (startDate) {
            updatePayload.date = formatDateWithoutTimezone(startDate);
          } else if (currentGift.date) {
            updatePayload.date = currentGift.date instanceof Date ? formatDateWithoutTimezone(currentGift.date) : currentGift.date;
          }
        } else if (fieldValue === 'Conditional') {
          // Include milestone and estimatedDate fields for Conditional occasions
          updatePayload.milestone = formData.milestone || currentGift.milestone || '';
          if (startDate) {
            updatePayload.milestoneEstimateDate = formatDateWithoutTimezone(startDate);
          } else if (currentGift.milestoneEstimateDate) {
            updatePayload.milestoneEstimateDate = currentGift.milestoneEstimateDate instanceof Date ?
              formatDateWithoutTimezone(currentGift.milestoneEstimateDate) : currentGift.milestoneEstimateDate;
          }
        }
      } else if (fieldName === 'deliverySchedule') {
        if (fieldValue === 'Recurring') {
          // Include frequency and numberOfYears fields for Recurring delivery
          updatePayload.frequency = formData.frequency || currentGift.frequency || '';
          updatePayload.numberOfYears = (formData.numberOfYears && parseInt(formData.numberOfYears)) || 
                                       (currentGift.numberOfYears && parseInt(currentGift.numberOfYears.toString())) || 
                                       null;
        }
      }


      const response = await axios.put(
        `${apiConfig.apiBaseURL}/gift/update-gift/${currentGift._id}`,
        updatePayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );


      if (response.data.status === 'Success') {

        // Update local context with all the fields that were sent
        updateGiftData(updatePayload);
      } else {
        throw new Error(response.data.message || 'Failed to update field');
      }
    } catch (error: any) {

      toast.error(`Failed to update ${fieldName}. Please try again.`);
    }
  };

  // Function to update all radio fields + related inputs using UPDATE API
  const updateRadioFieldsAndInputs = async () => {
    if (!currentGift?._id) {

      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const updatePayload: any = {};

      // Always include type and occasionType radio fields
      updatePayload.type = giftType;
      updatePayload.occasionType = occasionType;
      // Only include deliverySchedule when occasionType is not 'Conditional'
      if (occasionType !== 'Conditional') {
        updatePayload.deliverySchedule = deliverySchedule;
      }

      // Include related fields based on current selections
      if (giftType === 'Post-Mortem') {
        updatePayload.ssn = ssnDigits || currentGift?.ssn || '';

        // Include Primary Legacy Contact Information for Post-Mortem gifts
        if (primaryLegacyContact) {
          const { cityName, stateName } = await getLocationDisplayNames(primaryLegacyContact);
          
          updatePayload.primaryLegacyContact = {
            name: primaryLegacyContact.name,
            email: primaryLegacyContact.email,
            phoneNumber: primaryLegacyContact.phoneNumber,
            alternatePhoneNumber: primaryLegacyContact.alternatePhoneNumber || '',
            relationship: primaryLegacyContact.relationship,
            address1: primaryLegacyContact.address1 || '',
            address2: primaryLegacyContact.address2 || '',
            city: cityName || '',
            state: stateName || '',
            zip: primaryLegacyContact.zip || ''
          };
        }
      }

      if (occasionType === 'Fixed') {
        updatePayload.occasion = formData.occasion || currentGift.occasion || '';
        if (startDate) {
          updatePayload.date = formatDateWithoutTimezone(startDate);
        } else if (currentGift.date) {
          updatePayload.date = currentGift.date instanceof Date ? formatDateWithoutTimezone(currentGift.date) : currentGift.date;
        }
      } else if (occasionType === 'Conditional') {
        updatePayload.milestone = formData.milestone || currentGift.milestone || '';
        if (startDate) {
          updatePayload.milestoneEstimateDate = formatDateWithoutTimezone(startDate);
        } else if (currentGift.milestoneEstimateDate) {
          updatePayload.milestoneEstimateDate = currentGift.milestoneEstimateDate instanceof Date ?
            formatDateWithoutTimezone(currentGift.milestoneEstimateDate) : currentGift.milestoneEstimateDate;
        }
      }

      if (deliverySchedule === 'Recurring') {
        updatePayload.frequency = formData.frequency || currentGift.frequency || '';
        updatePayload.numberOfYears = (formData.numberOfYears && parseInt(formData.numberOfYears)) || 
                                     (currentGift.numberOfYears && parseInt(currentGift.numberOfYears.toString())) || 
                                     null;
      }


      const response = await axios.put(
        `${apiConfig.apiBaseURL}/gift/update-gift/${currentGift._id}`,
        updatePayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );


      if (response.data.status === 'Success') {

        // Update local context
        updateGiftData(updatePayload);
        // Reset the radio changes flag
        setHasRadioChanges(false);
      } else {
        throw new Error(response.data.message || 'Failed to update fields');
      }
    } catch (error: any) {

      toast.error('Failed to update gift. Please try again.');
    }
  };

  // Fetch states
  const fetchStates = async (): Promise<void> => {
    try {
      setLoadingStates(true);
      const response = await locationApi.getAllStates();
      
      if (response.status === 'Success' && response.states) {
        setStates(response.states);
      } else {
        toast.error('Failed to load states. Please refresh the page.');
      }
    } catch (error) {

      toast.error('Failed to load states. Please refresh the page.');
    } finally {
      setLoadingStates(false);
    }
  };

  // Handle state change and fetch cities
  const handleStateChange = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const selectedStateId = e.target.value;
    setRecipientData({ ...recipientData, state: selectedStateId, city: '' });
    setCities([]);

    if (selectedStateId) {
      try {
        setLoadingCities(true);
        const response = await locationApi.getCitiesByStateId(selectedStateId);
        
        if (response.status === 'Success' && response.cities) {
          setCities(response.cities);
        } else {
          toast.error('Failed to load cities for selected state.');
        }
      } catch (error) {

        toast.error('Failed to load cities for selected state.');
      } finally {
        setLoadingCities(false);
      }
    }
  };

  // Helper function to format date without timezone issues
  // Converts Date to YYYY-MM-DD format in local timezone
  const formatDateWithoutTimezone = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchContacts = async (): Promise<void> => {
    try {
      setLoadingContacts(true);
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/contacts/get-all-contacts`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          params: {
            page: 1,
            limit: 100
          }
        }
      );

      if (response.data.status === 'Success') {
        setContacts(response.data.data);
      } else {
        toast.error('Failed to fetch contacts');
      }
    } catch (error) {

      toast.error('Failed to fetch contacts. Please try again.');
    } finally {
      setLoadingContacts(false);
    }
  };


  // Validation functions
  const validateMainForm = (): FormErrors => {
    const errors: FormErrors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Gift title is required';
    }
    
    if (!formData.recipientId) {
      errors.recipientId = 'Please select a recipient';
    }
    
    if (occasionType === "Fixed") {
      if (!formData.occasion.trim()) {
        errors.occasion = 'Occasion is required';
      }
      if (!startDate) {
        errors.date = 'Date is required';
      }
    }
    
    if (occasionType === "Conditional") {
      if (!formData.milestone.trim()) {
        errors.milestone = 'Milestone is required';
      }
      if (!startDate) {
        errors.estimatedDate = 'Estimated date is required';
      }
    }
    
    if (giftType === "Post-Mortem") {
      // Only validate SSN if user hasn't entered it before
      if (!userHasEnteredSSN) {
        if (!ssnDigits) {
          errors.ssn = 'SSN is required for post-mortem gifts';
        }
        if (!ssnConfirmationDigits) {
          errors.ssnConfirmation = 'SSN confirmation is required for post-mortem gifts';
        }
        if (ssnDigits && ssnConfirmationDigits) {
          if (ssnDigits !== ssnConfirmationDigits) {
            errors.ssnConfirmation = 'SSN numbers do not match';
          }
        }
      }
    }
    
    // Only validate recurring fields for Fixed occasions
    if (occasionType === "Fixed" && deliverySchedule === "Recurring") {
      if (!formData.frequency) {
        errors.frequency = 'Frequency is required';
      }
      
      // Enhanced validation for numberOfYears as text field
      if (!formData.numberOfYears?.trim()) {
        errors.numberOfYears = 'Number of years is required';
      } else {
        const yearValue = parseInt(formData.numberOfYears);
        if (isNaN(yearValue) || yearValue < 1) {
          errors.numberOfYears = 'Number of years must be at least 1';
        } else if (yearValue > 50) {
          errors.numberOfYears = 'Number of years cannot exceed 50';
        }
      }
    }
    
    return errors;
  };

  const validateRecipientForm = (): FormErrors => {
    const errors: FormErrors = {};
    
    if (!recipientData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!recipientData.relationship.trim()) {
      errors.relationship = 'Relationship is required';
    }
    
    if (!recipientData.address1.trim()) {
      errors.address1 = 'Address 1 is required';
    }
    
    if (!recipientData.city) {
      errors.city = 'City is required';
    }
    
    if (!recipientData.state) {
      errors.state = 'State is required';
    }
    
    if (!recipientData.zip.trim()) {
      errors.zip = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(recipientData.zip)) {
      errors.zip = 'Please enter a valid ZIP code';
    }
    
    if (!recipientData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!recipientData.phone || recipientData.phone === '+1') {
      errors.phone = 'Phone number is required';
    } else {
      const digits = getRawPhoneDigits(recipientData.phone);
      if (digits.length !== 10) {
        errors.phone = 'Phone number must be exactly 10 digits';
      }
    }
    
    // Validate alternate phone if provided
    if (recipientData.altPhone && recipientData.altPhone !== '+1') {
      const altDigits = getRawPhoneDigits(recipientData.altPhone);
      if (altDigits.length !== 10) {
        errors.altPhone = 'Alternate phone number must be exactly 10 digits';
      }
    }
    
    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    
    // Apply sanitizers based on field type
    let sanitizedValue = value;
    if (name === 'name' || name === 'relationship') {
      sanitizedValue = enhancedSanitizers.name(value);
    } else if (name === 'email') {
      sanitizedValue = enhancedSanitizers.email(value);
    } else if (name === 'zip') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'address1' || name === 'address2') {
      // Address fields should allow all characters including spaces, numbers, special chars
      sanitizedValue = value; // No sanitization for address fields
    } else {
      sanitizedValue = enhancedSanitizers.text(value);
    }
    
    setRecipientData({ ...recipientData, [name]: sanitizedValue });
    
    // Clear error when user starts typing
    if (recipientErrors[name]) {
      setRecipientErrors({ ...recipientErrors, [name]: '' });
    }
  };

  const handleFormDataChange = (field: keyof GiftFormData, value: string): void => {
    let sanitizedValue = value;
    
    // Apply minimal sanitization during typing - only for specific fields that need it
    if (field === 'numberOfYears') {
      // Only allow numbers for numberOfYears, limit to reasonable range
      const numericValue = value.replace(/\D/g, '');
      // Limit to 2 digits (max 99 years)
      sanitizedValue = numericValue.slice(0, 2);
    } else {
      // For text fields (title, occasion, milestone), allow all characters including spaces
      // No sanitization during typing - only basic validation on form submission
      sanitizedValue = value;
    }
    
    setFormData({ ...formData, [field]: sanitizedValue });
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  // Simple SSN storage - just store the actual digits
  const [ssnDigits, setSsnDigits] = useState<string>('');
  const [ssnConfirmationDigits, setSsnConfirmationDigits] = useState<string>('');
  
  // Debug: Log when SSN digits change
  useEffect(() => {

  }, [ssnDigits]);
  
  useEffect(() => {

  }, [ssnConfirmationDigits]);

  // SSN masking function that always masks first 5 digits
  const maskSSN = (ssn: string): string => {
    if (!ssn) return '';
    const cleanSSN = ssn.replace(/\D/g, '');
    
    let masked = '';
    for (let i = 0; i < cleanSSN.length; i++) {
      if (i < 5) {
        masked += '*';
      } else {
        masked += cleanSSN[i];
      }
    }
    
    // Format with dashes
    if (masked.length >= 6) {
      masked = `${masked.slice(0, 3)}-${masked.slice(3, 5)}-${masked.slice(5, 9)}`;
    } else if (masked.length >= 3) {
      masked = `${masked.slice(0, 3)}-${masked.slice(3)}`;
    }
    
    return masked;
  };

  // Simple function to format SSN with masking
  const formatSSNDisplay = (digits: string): string => {

    
    if (!digits) return '';
    
    let masked = '';
    for (let i = 0; i < digits.length; i++) {
      if (i < 5) {
        masked += '*';
      } else {
        masked += digits[i];
      }
    }
    
    // Add dashes
    if (masked.length >= 6) {
      const formatted = `${masked.slice(0, 3)}-${masked.slice(3, 5)}-${masked.slice(5)}`;
      return formatted;
    } else if (masked.length >= 3) {
      const formatted = `${masked.slice(0, 3)}-${masked.slice(3)}`;
      return formatted;
    }
    
    return masked;
  };

  // Simple SSN input handlers for password fields
  const handleSSNInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);

    setSsnDigits(digits);
    
    // Clear error
    if (formErrors.ssn) {
      setFormErrors(prev => ({ ...prev, ssn: '' }));
    }
  };

  const handleSSNConfirmationInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);

    setSsnConfirmationDigits(digits);
    
    // Clear error
    if (formErrors.ssnConfirmation) {
      setFormErrors(prev => ({ ...prev, ssnConfirmation: '' }));
    }
  };

  // Create phone change handlers
  const handlePhoneChange = createPhoneChangeHandler({ 
    setFieldValue: (field: string, value: string) => {
      setRecipientData({ ...recipientData, [field]: value });
      if (recipientErrors[field]) {
        setRecipientErrors({ ...recipientErrors, [field]: '' });
      }
    }
  }, 'phone');

  const handleAltPhoneChange = createPhoneChangeHandler({ 
    setFieldValue: (field: string, value: string) => {
      setRecipientData({ ...recipientData, [field]: value });
      if (recipientErrors[field]) {
        setRecipientErrors({ ...recipientErrors, [field]: '' });
      }
    }
  }, 'altPhone');

  const handleAddClick = (): void => {
    setRecipientData({
      name: '',
      relationship: '',
      address1: '',
      address2: '',
      state: '',
      city: '',
      zip: '',
      email: '',
      phone: '+1',
      altPhone: '+1',
    });
    setRecipientErrors({});
    setRecipientTouched({});
    setCities([]);
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleEditClick = (): void => {
    if (selectedRecipient) {
      // Find the state by name to get its ID
      const selectedState = states.find(state => state.name === selectedRecipient.state);
      
      setRecipientData({
        name: selectedRecipient.name || '',
        relationship: selectedRecipient.relationship || '',
        address1: selectedRecipient.address1 || '',
        address2: selectedRecipient.address2 || '',
        state: selectedState ? selectedState._id : '',
        city: selectedRecipient.city || '',
        zip: selectedRecipient.zip || '',
        email: selectedRecipient.email || '',
        phone: selectedRecipient.phoneNumber ? enhancedSanitizers.phone('+1' + selectedRecipient.phoneNumber) : '+1',
        altPhone: selectedRecipient.alternatePhoneNumber ? enhancedSanitizers.phone('+1' + selectedRecipient.alternatePhoneNumber) : '+1',
      });
      
      // Load cities for the selected state
      if (selectedState) {
        loadCitiesForEdit(selectedState._id, selectedRecipient.city);
      }
      
      setRecipientErrors({});
      setRecipientTouched({});
      setIsEditMode(true);
      setShowModal(true);
    }
  };

  // Helper function to load cities for edit mode
  const loadCitiesForEdit = async (stateId: string, cityName: string): Promise<void> => {
    try {
      setLoadingCities(true);
      const response = await locationApi.getCitiesByStateId(stateId);
      
      if (response.status === 'Success' && response.cities) {
        setCities(response.cities);
        
        // Find and set the city ID based on city name
        if (cityName) {
          const selectedCity = response.cities.find(city => city.name === cityName);
          if (selectedCity) {
            setRecipientData(prev => ({ ...prev, city: selectedCity._id }));
          }
        }
      }
    } catch (error) {

      toast.error('Failed to load cities for selected state.');
    } finally {
      setLoadingCities(false);
    }
  };

  const handleModalSubmit = async (): Promise<void> => {
    const errors = validateRecipientForm();
    
    if (Object.keys(errors).length > 0) {
      setRecipientErrors(errors);
      setRecipientTouched({
        name: true,
        relationship: true,
        address1: true,
        city: true,
        state: true,
        zip: true,
        email: true,
        phone: true,
        altPhone: true
      });
      
      // Scroll to first error field instead of showing toast
      const firstErrorField = Object.keys(errors)[0];
      setTimeout(() => {
        const element = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        } else {
          // Try finding by label text or nearby elements
          const labelElement = document.querySelector(`label:contains("${firstErrorField}")`) as HTMLElement;
          if (labelElement) {
            labelElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      
      // Get state and city names from IDs
      const selectedState = states.find(state => state._id === recipientData.state);
      const selectedCity = cities.find(city => city._id === recipientData.city);
      
      const contactData = {
        name: recipientData.name.trim(),
        relationship: recipientData.relationship.trim(),
        address1: recipientData.address1.trim(),
        address2: recipientData.address2?.trim() || '',
        city: selectedCity ? selectedCity.name : recipientData.city,
        state: selectedState ? selectedState.name : recipientData.state,
        zip: recipientData.zip.trim(),
        email: recipientData.email.trim().toLowerCase(),
        phoneNumber: getRawPhoneDigits(recipientData.phone),
        alternatePhoneNumber: getRawPhoneDigits(recipientData.altPhone) || ''
      };

      let response;
      if (isEditMode) {
        // Determine which API to use based on gift object state
        // If gift has an ID and has stored values, use gift API
        // If gift is empty (just started), use contact API
        const hasGiftData = currentGift?._id;

        if (hasGiftData) {
          // Use gift API - gift/update-gift-contact/:contactId
          const formDataObj = new FormData();

          // Add giftId to payload
          formDataObj.append('giftId', currentGift._id);

          // Append all contact data fields (including empty ones for required fields)
          Object.entries(contactData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formDataObj.append(key, value.toString());
            }
          });


          response = await axios.put(
            `${apiConfig.apiBaseURL}/gift/update-gift-contact/${selectedRecipient?._id}`,
            formDataObj,
            {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            }
          );
        } else {
          // Use contact API - contacts/update-contact/:contactId
          const formDataObj = new FormData();

          // Append all contact data fields
          Object.entries(contactData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formDataObj.append(key, value.toString());
            }
          });


          response = await axios.put(
            `${apiConfig.apiBaseURL}/contacts/update-contact/${selectedRecipient?._id}`,
            formDataObj,
            {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            }
          );
        }
      } else {
        // Use FormData for create
        const formDataObj = new FormData();
        Object.entries(contactData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            formDataObj.append(key, value.toString());
          }
        });

        response = await axios.post(
          `${apiConfig.apiBaseURL}/contacts/add-contact`,
          formDataObj,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          }
        );
      }

      if (response.data.status === 'Success') {
        toast.success(isEditMode ? 'Contact updated successfully!' : 'Contact added successfully!');
        await fetchContacts();

        // Reload gift data after editing to update recipient info in step 4
        if (isEditMode && currentGift?._id) {
          await loadGift(currentGift._id, false);
        }

        if (!isEditMode && response.data.contact) {
          setFormData({ ...formData, recipientId: response.data.contact._id });
        }
        setShowModal(false);
      } else {
        toast.error(response.data.message || `Failed to ${isEditMode ? 'update' : 'add'} contact`);
      }
    } catch (error: any) {

      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} contact. Please try again.`);
    }
  };

  // Handle save as draft - Updated to use the context's saveGiftAsDraft function
  const handleSaveAsDraft = async (): Promise<void> => {
    try {
      const giftData: Partial<CreateGiftRequest> = {
        title: formData.title.trim() || 'Untitled Gift',
        recipientId: formData.recipientId || '',
        type: giftType,
        occasionType: occasionType,
        // Only include deliverySchedule when occasionType is not 'Conditional'
        ...(occasionType !== 'Conditional' && { deliverySchedule: deliverySchedule }),
        additionalComments: formData.additionalComments.trim()
      };

      // CRITICAL FIX: Always include all fields, even if empty/null to properly clear previous values
      if (occasionType === "Fixed") {
        giftData.occasion = formData.occasion.trim() || '';
        giftData.date = startDate ? formatDateWithoutTimezone(startDate) : null;
        // Clear conditional fields when in Fixed mode
        giftData.milestone = '';
        giftData.milestoneEstimateDate = null;

        if (deliverySchedule === "Recurring") {
          // CRITICAL FIX: Also set as top-level fields
          giftData.frequency = formData.frequency || '';
          giftData.numberOfYears = formData.numberOfYears ? parseInt(formData.numberOfYears) : null;
        } else {
          // Clear recurring fields when not in Recurring mode
          giftData.frequency = '';
          giftData.numberOfYears = null;
        }
      } else if (occasionType === "Conditional") {
        giftData.milestone = formData.milestone.trim() || '';
        giftData.milestoneEstimateDate = startDate ? formatDateWithoutTimezone(startDate) : null;
        // Clear fixed fields when in Conditional mode
        giftData.occasion = '';
        giftData.date = null;
        // No recurring for conditional
        giftData.frequency = '';
        giftData.numberOfYears = null;
      }

      if (giftType === "Post-Mortem") {
        // Use stored SSN from profile if already entered, otherwise use newly entered SSN
        if (userHasEnteredSSN && userProfileSSN) {
          giftData.ssn = userProfileSSN.replace(/\D/g, '');
        } else {
          giftData.ssn = ssnDigits;
        }

        // For post-mortem gifts, override date format to MM-DD
        if (startDate) {
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          giftData.date = `${month}-${day}`;
        }

        // For recurring post-mortem gifts, add frequency and numberOfYears
        if (deliverySchedule === "Recurring") {
          // CRITICAL FIX: Also set as top-level fields
          giftData.frequency = formData.frequency;
          giftData.numberOfYears = parseInt(formData.numberOfYears);
        }

        if (primaryLegacyContact) {
          // Get resolved city and state names
          const { cityName, stateName } = await getLocationDisplayNames(primaryLegacyContact);
          
          giftData.primaryLegacyContact = {
            name: primaryLegacyContact.name,
            email: primaryLegacyContact.email,
            phoneNumber: primaryLegacyContact.phoneNumber,
            alternatePhoneNumber: primaryLegacyContact.alternatePhoneNumber || '',
            relationship: primaryLegacyContact.relationship,
            address1: primaryLegacyContact.address1 || '',
            address2: primaryLegacyContact.address2 || '',
            city: cityName || '',
            state: stateName || '',
            zip: primaryLegacyContact.zip || ''
          };
        }
      }

      // Update the current gift context first
      const updateData = {
        title: giftData.title,
        recipientId: giftData.recipientId,
        type: giftData.type,
        occasionType: giftData.occasionType,
        deliverySchedule: giftData.deliverySchedule,
        occasion: giftData.occasion || '',
        milestone: giftData.milestone || '',
        date: startDate,
        milestoneEstimateDate: giftData.milestoneEstimateDate ? new Date(giftData.milestoneEstimateDate) : null,
        frequency: giftData.frequency || '',
        numberOfYears: String(giftData.numberOfYears || formData.numberOfYears),
        additionalComments: giftData.additionalComments || '',
        ssn: ssnDigits || '',
        primaryLegacyContact: giftData.primaryLegacyContact,
        status: 'Draft' as 'Draft' | 'Scheduled' | 'Delivered'
      };
      
      
      updateGiftData(updateData);

      // Call the context's saveGiftAsDraft function
      await saveGiftAsDraft(giftData);
      
    } catch (error) {

      toast.error('Failed to save draft');
    }
  };

  // Helper function to scroll to first error field  
  const scrollToFirstError = (errors: FormErrors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;
    
    const firstErrorField = errorFields[0];
    
    // Map field names to more specific selectors
    const fieldSelectors = {
      'title': 'input[name="title"]',
      'recipientId': '.recipient-select input, .recipient-select',
      'occasion': '.occasion-select input, .occasion-select',
      'milestone': '.milestone-select input, .milestone-select', 
      'date': 'input[type="date"]',
      'estimatedDate': 'input[name="estimatedDate"]',
      'ssn': 'input[type="password"]:first-of-type',
      'ssnConfirmation': 'input[type="password"]:last-of-type',
      'frequency': '.frequency-select input, .frequency-select',
      'numberOfYears': 'input[name="numberOfYears"]'
    };
    
    const selector = fieldSelectors[firstErrorField as keyof typeof fieldSelectors] || `[name="${firstErrorField}"]`;
    
    setTimeout(() => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        
        // Add visual highlight
        element.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.3)';
        setTimeout(() => {
          element.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  };

  const handleCreateGift = async (): Promise<void> => {
    const errors = validateMainForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTouched({
        title: true,
        recipientId: true,
        occasion: true,
        milestone: true,
        date: true,
        estimatedDate: true,
        ssn: true,
        ssnConfirmation: true,
        frequency: true,
        numberOfYears: true
      });
      
      // Scroll to first error field instead of showing toast
      scrollToFirstError(errors);
      return;
    }

    // Check if we're updating an existing gift or creating a new one
    const isUpdating = currentGift?._id;

    const requestData: CreateGiftRequest = {
      title: formData.title.trim(),
      recipientId: formData.recipientId,
      type: giftType,
      occasionType: occasionType,
      // Only include deliverySchedule when occasionType is not 'Conditional'
      ...(occasionType !== 'Conditional' && { deliverySchedule: deliverySchedule }),
      additionalComments: formData.additionalComments.trim()
    };

    if (occasionType === "Fixed") {
      requestData.occasion = formData.occasion.trim();
      if (startDate) requestData.date = formatDateWithoutTimezone(startDate);
      
      if (deliverySchedule === "Recurring") {
        // FIXED: Also include legacy flat fields for backward compatibility
        requestData.frequency = formData.frequency;
        requestData.numberOfYears = parseInt(formData.numberOfYears);
      }
    } else if (occasionType === "Conditional") {
      requestData.milestone = formData.milestone.trim();
      if (startDate) requestData.milestoneEstimateDate = formatDateWithoutTimezone(startDate);
    }

    if (giftType === "Post-Mortem") {
      // Use stored SSN from profile if already entered, otherwise use newly entered SSN
      if (userHasEnteredSSN && userProfileSSN) {
        requestData.ssn = userProfileSSN.replace(/\D/g, '');

      } else {
        requestData.ssn = ssnDigits;
      }

      if (primaryLegacyContact) {
        // Get resolved city and state names
        const { cityName, stateName } = await getLocationDisplayNames(primaryLegacyContact);
        
        requestData.primaryLegacyContact = {
          name: primaryLegacyContact.name,
          email: primaryLegacyContact.email,
          phoneNumber: primaryLegacyContact.phoneNumber,
          alternatePhoneNumber: primaryLegacyContact.alternatePhoneNumber || '',
          relationship: primaryLegacyContact.relationship,
          address1: primaryLegacyContact.address1 || '',
          address2: primaryLegacyContact.address2 || '',
          city: cityName || '',
          state: stateName || '',
          zip: primaryLegacyContact.zip || ''
        };
      } else {
        toast.error('Please set a primary legacy contact before creating a post-mortem gift.');
        return;
      }
      
      // For post-mortem gifts, we ALWAYS need occasion and date, regardless of occasion type
      if (!requestData.occasion) {
        if (occasionType === "Fixed") {
          requestData.occasion = formData.occasion.trim();
        } else if (occasionType === "Conditional") {
          requestData.occasion = formData.milestone.trim(); // Use milestone as occasion for conditional
        }
      }
      
      // For post-mortem gifts, override the date format to MM-DD regardless of what was set before
      if (startDate) {
        // For post-mortem gifts, convert date to MM-DD format
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        requestData.date = `${month}-${day}`;
      } else {
        // Date is required - scroll to date field
        setTimeout(() => {
          const dateField = document.querySelector('input[type="date"]') as HTMLElement;
          if (dateField) {
            dateField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            dateField.focus();
            dateField.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.3)';
            setTimeout(() => { dateField.style.boxShadow = ''; }, 2000);
          }
        }, 100);
        return;
      }
      
      // For recurring post-mortem gifts, add frequency and numberOfYears
      if (deliverySchedule === "Recurring") {
        // FIXED: Also include legacy flat fields for backward compatibility
        requestData.frequency = formData.frequency;
        requestData.numberOfYears = parseInt(formData.numberOfYears);
        
        // Validate recurring fields for post-mortem gifts
        if (!requestData.frequency) {
          // Frequency is required - scroll to frequency field
          setTimeout(() => {
            const frequencyField = document.querySelector('select[name="frequency"]') as HTMLElement;
            if (frequencyField) {
              frequencyField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              frequencyField.focus();
              frequencyField.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.3)';
              setTimeout(() => { frequencyField.style.boxShadow = ''; }, 2000);
            }
          }, 100);
          return;
        }
        
        // Enhanced validation for numberOfYears in post-mortem gifts
        if (!formData.numberOfYears?.trim()) {
          // Number of years is required - scroll to numberOfYears field
          setTimeout(() => {
            const yearsField = document.querySelector('input[name="numberOfYears"]') as HTMLElement;
            if (yearsField) {
              yearsField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              yearsField.focus();
              yearsField.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.3)';
              setTimeout(() => { yearsField.style.boxShadow = ''; }, 2000);
            }
          }, 100);
          return;
        }
        
        const yearValue = parseInt(formData.numberOfYears);
        if (isNaN(yearValue) || yearValue < 1) {
          toast.error('Number of years must be at least 1 for recurring post-mortem gifts.');
          return;
        }
        if (yearValue > 50) {
          toast.error('Number of years cannot exceed 50 for recurring post-mortem gifts.');
          return;
        }
      }
      
      // Validate that both occasion and date are now set
      if (!requestData.occasion || !requestData.date) {
        // Occasion and date are required - scroll to occasion field
        setTimeout(() => {
          const occasionField = document.querySelector('select[name="occasion"]') as HTMLElement;
          if (occasionField) {
            occasionField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            occasionField.focus();
            occasionField.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.3)';
            setTimeout(() => { occasionField.style.boxShadow = ''; }, 2000);
          }
        }, 100);
        return;
      }
    }


    if (isUpdating) {
      // SMART API SELECTION: Choose the correct endpoint based on what changed
      if (hasRadioChanges) {
        // If any radio fields changed, use UPDATE API with all radio fields + related inputs

        await updateRadioFieldsAndInputs();
      } else {
        // If only text fields changed, use SAVE DRAFT API

        await handleSaveAsDraft();
      }

      // Mark Step 1 as completed
      markStepCompleted('1');

      // Navigate to next step
      if (onNext) {
        onNext();
      }
    } else {
      // If creating new gift, use createGift function

      const newGift = await createGift(requestData);
      if (newGift) {
        // Mark Step 1 as completed
        markStepCompleted('1');

        if (onNext) {
          onNext();
        }
      }
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className='step-form gift-info-main'>
      {/* <div style={{
        background: '#EEF4FB',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '25px',
        border: '1px solid #D4E4F7'
      }}>
        <p style={{
          fontSize: '16px',
          color: '#3957A0',
          margin: 0,
          lineHeight: '1.6',
          fontWeight: '500'
        }}>
          You're setting up when and how your gift and recorded message will be delivered. You can edit everything later.
        </p>
      </div> */}
      <h2 className='form-title'>Gift Information
        <span style={{
          fontSize: '16px',
          color: '#000',
          margin: 0,
          lineHeight: '1.6',
          fontWeight: '500',
          width: '100%',
          display: 'block',
        }}>
          You're setting up when and how your gift and recorded message will be delivered. You can edit everything later.
        </span>
      </h2>
      
      <Form>
        <div className='row'>
          <FormGroup className='form-group col-md-6'>
            <FormLabel>Gift Title *</FormLabel>
            <FormControl
              type="text"
              name="title"
              placeholder="Enter Gift Title"
              value={formData.title}
              onChange={(e) => handleFormDataChange('title', e.target.value)}
            />
            {touched.title && formErrors.title && (
              <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                {formErrors.title}
              </small>
            )}
          </FormGroup>

          <FormGroup className='form-group col-md-6 position-relative'>
            <span className='add-new' onClick={handleAddClick} role="button">
              <FiPlus />
              <span>Add New Recipient</span>
            </span>
            <FormLabel>Select Recipient *</FormLabel>
            <CustomSelect
              name="recipientId"
              value={formData.recipientId}
              onChange={(value) => handleFormDataChange('recipientId', value)}
              options={contacts.map(contact => ({
                value: contact._id,
                label: `${contact.name} (${contact.relationship})`
              }))}
              placeholder="Select Recipient"
              loading={loadingContacts}
              loadingText="Loading recipients..."
              className="recipient-select"
            />
            {touched.recipientId && formErrors.recipientId && (
              <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                {formErrors.recipientId}
              </small>
            )}
          </FormGroup>

          {selectedRecipient && (
            <div className='recipient-info'>
              <div className='recipient-info-in'>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  Recipient Information
                  <Button onClick={handleEditClick} style={{ background: 'none', border: 'none', padding: '0', marginLeft: '0' }}>
                    <img src={EditIcon} alt='' style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                  </Button>
                </h3>
                <ul className='icon-list'>
                  <li>
                    <img src={ProfileIcon} alt='' />
                    <span>{selectedRecipient.name} ({selectedRecipient.relationship})</span>
                  </li>
                  <li>
                    <img src={MailIcon} alt='' />
                    <span>{selectedRecipient.email}</span>
                  </li>
                  <li>
                    <img src={CallIcon} alt='' />
                    <span>
                      {formatPhoneNumber(selectedRecipient.phoneNumber)}
                      {selectedRecipient.alternatePhoneNumber && (
                        <>
                          <img src={CallIcon} alt='' style={{ marginLeft: '15px', marginRight: '5px' }} />
                          {formatPhoneNumber(selectedRecipient.alternatePhoneNumber)}
                        </>
                      )}
                    </span>
                  </li>
                  <li>
                    <img src={AddressIcon} alt='' />
                    <span>
                      {(() => {
                        const { cityName, stateName } = getLocationDisplayNamesSync(selectedRecipient);
                        return [selectedRecipient.address1, selectedRecipient.address2, 
                          cityName, stateName, selectedRecipient.zip]
                          .filter(Boolean).join(', ');
                      })()}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Gift Type Section */}
          <FormGroup className='form-group col-md-12 radio-buttons'>
            <span className='custom-label'>Gift Type * </span>
            <div className='d-flex'>
              <FormGroup className='form-group'>
                <Form.Check
                  type="radio"
                  id="regular"
                  label="Regular Gift"
                  name="giftType"
                  checked={giftType === "Regular"}
                  onChange={() => {
                    // Clear date when switching gift types (user interaction only)
                    if (giftType !== "Regular") {
                      setStartDate(null);
                    }
                    setGiftType("Regular");
                    setHasRadioChanges(true);
                  }}
                />
              </FormGroup>
              <FormGroup className='form-group'>
                <Form.Check
                  type="radio"
                  id="postmortem"
                  name="giftType"
                  checked={giftType === "Post-Mortem"}
                  onChange={() => {
                    // Clear date when switching gift types (user interaction only)
                    if (giftType !== "Post-Mortem") {
                      setStartDate(null);
                    }
                    setGiftType("Post-Mortem");
                    setHasRadioChanges(true);
                  }}
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Post-Mortem Gift
                      {giftType === "Post-Mortem" && userHasEnteredSSN && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#d4edda',
                          border: '1px solid #c3e6cb',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#155724'
                        }}>
                          <img src={VerifiedIcon} alt="Verified" style={{ width: '18px', height: '18px' }} />
                          SSN Verified
                        </span>
                      )}
                    </span>
                  }
                />
              </FormGroup>
            </div>
          </FormGroup>

          {/* Show Post-Mortem info when selected */}
          {giftType === "Post-Mortem" && (
            <div className="alert alert-info py-1" style={{ background: 'linear-gradient(135deg, rgb(248, 249, 255) 0%, rgb(231, 236, 241) 100%)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(57, 87, 160, 0.1)', boxShadow: 'rgba(57, 87, 160, 0.08) 0px 4px 20px', color: '#333333', fontStyle: 'italic' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiInfo className="my-auto" style={{ flexShrink: 0 }} />
                <small>
                  <strong>Post-Mortem Gift:</strong> Post-Mortem gifts are only delivered after verification, exactly as you specify.
                </small>
              </div>
            </div>
          )}

          {/* Show SSN fields for Post-Mortem gifts */}
          {giftType === "Post-Mortem" && (
            <>
              {!userHasEnteredSSN && (
                // Show SSN input fields if not entered before
                <>
                  <FormGroup className='form-group col-md-6'>
                    <FormLabel>Social Security Number *</FormLabel>
                    <div style={{ position: 'relative' }}>
                      <FormControl
                        type="password"
                        placeholder="Enter 9 digits"
                        value={ssnDigits}
                        onChange={handleSSNInput}
                        maxLength={9}
                        isInvalid={touched.ssn && !!formErrors.ssn}
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '2px'
                        }}
                      />
                      {ssnDigits.length >= 4 && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          fontFamily: 'monospace',
                          pointerEvents: 'none',
                          backgroundColor: 'white',
                          padding: '0 4px'
                        }}>
                          ***-**-{ssnDigits.slice(-4)}
                        </div>
                      )}
                    </div>
                    {touched.ssn && formErrors.ssn && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                        {formErrors.ssn}
                      </small>
                    )}
                  </FormGroup>

                  <FormGroup className='form-group col-md-6'>
                    <FormLabel>Confirm Social Security Number *</FormLabel>
                    <div style={{ position: 'relative' }}>
                      <FormControl
                        type="password"
                        placeholder="Re-enter 9 digits"
                        value={ssnConfirmationDigits}
                        onChange={handleSSNConfirmationInput}
                        maxLength={9}
                        isInvalid={touched.ssnConfirmation && !!formErrors.ssnConfirmation}
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '2px'
                        }}
                      />
                      {ssnConfirmationDigits.length >= 4 && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          fontFamily: 'monospace',
                          pointerEvents: 'none',
                          backgroundColor: 'white',
                          padding: '0 4px'
                        }}>
                          ***-**-{ssnConfirmationDigits.slice(-4)}
                        </div>
                      )}
                    </div>
                    {touched.ssnConfirmation && formErrors.ssnConfirmation && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                        {formErrors.ssnConfirmation}
                      </small>
                    )}
                  </FormGroup>
                </>
              )}
            </>
          )}

          {/* Display Primary Legacy Contact for Post-Mortem gifts */}
          {giftType === "Post-Mortem" && primaryLegacyContact && (
            <div className='recipient-info'>
              <div className='recipient-info-in'>
                <h3>Primary Legacy Contact Information</h3>
                <ul className='icon-list'>
                  <li>
                    <img src={ProfileIcon} alt='' />
                    <span>{primaryLegacyContact.name} ({primaryLegacyContact.relationship})</span>
                  </li>
                  <li>
                    <img src={MailIcon} alt='' />
                    <span>{primaryLegacyContact.email}</span>
                  </li>
                  <li>
                    <img src={CallIcon} alt='' />
                    <span>
                      {formatPhoneNumber(primaryLegacyContact.phoneNumber)}
                      {primaryLegacyContact.alternatePhoneNumber && 
                        `, ${formatPhoneNumber(primaryLegacyContact.alternatePhoneNumber)}`
                      }
                    </span>
                  </li>
                  <li>
                    <img src={AddressIcon} alt='' />
                    <span>
                      {(() => {
                        const { cityName, stateName } = getLocationDisplayNamesSync(primaryLegacyContact);
                        return [primaryLegacyContact.address1, primaryLegacyContact.address2, 
                          cityName, stateName, primaryLegacyContact.zip]
                          .filter(Boolean).join(', ');
                      })()}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Show warning if Post-Mortem is selected but no primary legacy contact */}
          {giftType === "Post-Mortem" && !primaryLegacyContact && (
            <div className='col-md-12'>
              <div className="alert alert-warning" role="alert">
                <strong>Warning:</strong> No primary legacy contact found. Please set a primary legacy contact in your contacts before creating a post-mortem gift.
              </div>
            </div>
          )}

          {/* Occasion Settings */}
          <FormGroup className='form-group col-md-12 radio-buttons'>
            <span className='custom-label'>Set Occasion *</span>
            {/* Hide Fixed and Conditional radio buttons for Post-Mortem gifts */}
            {giftType !== "Post-Mortem" && (
              <div className='d-flex'>
                <FormGroup className='form-group'>
                  <Form.Check
                    type="radio"
                    label="Fixed"
                    name="occasionType2"
                    checked={occasionType === "Fixed"}
                    onChange={() => {
                      setOccasionType("Fixed");
                      setHasRadioChanges(true);
                    }}
                  />
                </FormGroup>
                <FormGroup className='form-group'>
                  <Form.Check
                    type="radio"
                    label="Conditional"
                    name="occasionType2"
                    checked={occasionType === "Conditional"}
                    onChange={() => {
                      setOccasionType("Conditional");
                      setHasRadioChanges(true);
                    }}
                  />
                </FormGroup>
              </div>
            )}
          </FormGroup>

          {/* Show occasion info for both Fixed and Conditional (hide for Post-Mortem) */}
          {giftType !== "Post-Mortem" && (
            <div className="alert alert-info py-1" style={{ background: 'linear-gradient(135deg, rgb(248, 249, 255) 0%, rgb(231, 236, 241) 100%)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(57, 87, 160, 0.1)', boxShadow: 'rgba(57, 87, 160, 0.08) 0px 4px 20px', color: '#333333', fontStyle: 'italic' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiInfo className="my-auto" style={{ flexShrink: 0 }} />
                <small>
                  Fixed delivers on a specific date. Conditional delivers when a life milestone or verification event occures.
                </small>
              </div>
            </div>
          )}

          {/* Show these fields if Fixed is selected */}
          {occasionType === "Fixed" && (
            <>
              <FormGroup className='form-group col-md-6'>
                <FormLabel>Occasion *</FormLabel>
                <FormControl
                  type="text"
                  placeholder="Enter Occasion (e.g., Birthday, Anniversary)"
                  value={formData.occasion}
                  onChange={(e) => handleFormDataChange('occasion', e.target.value)}
                />
                {touched.occasion && formErrors.occasion && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.occasion}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group date-picker col-md-6'>
                <FormLabel>Date *</FormLabel>
                <div className="position-relative">
                  <DatePicker
                    ref={datePickerRef}
                    value={startDate}
                    onChange={(dateObject: DateObject | null) => {
                      try {
                        if (dateObject) {
                          // Convert DateObject to JavaScript Date
                          const jsDate = dateObject.toDate();

                          if (giftType === 'Post-Mortem') {
                            // For Post-Mortem, create a date object with current year but selected month/day
                            const postMortemDate = new Date(new Date().getFullYear(), jsDate.getMonth(), jsDate.getDate());
                            setStartDate(postMortemDate);
                          } else {
                            setStartDate(jsDate);
                          }
                        } else {
                          setStartDate(null);
                        }

                        if (formErrors.date) {
                          setFormErrors({ ...formErrors, date: '' });
                        }
                      } catch (error) {

                        setStartDate(null);
                      }
                    }}
                    format={giftType === 'Post-Mortem' ? "MM/DD" : "MM/DD/YYYY"}
                    className={`form-control ${touched.date && formErrors.date ? 'is-invalid' : ''}`}
                    minDate={giftType === 'Post-Mortem' ? undefined : new Date()}
                    placeholder="Select a date"
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ width: "100%", height: "48px", padding: "12px 45px 12px 16px" }}
                    onlyMonthPicker={giftType === 'Post-Mortem' ? false : undefined}
                    onlyYearPicker={false}
                    editable={false}
                    plugins={[]}
                  />
                  <img
                    src={calendarIcon}
                    alt="Calendar Icon"
                    className="calendar-icon"
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 2,
                      width: "20px",
                      height: "20px",
                      pointerEvents: "none"
                    }}
                  />
                </div>
                {touched.date && formErrors.date && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.date}
                  </small>
                )}
              </FormGroup>
            </>
          )}

          {/* Show these fields if Conditional is selected */}
          {occasionType === "Conditional" && (
            <>
              <FormGroup className='form-group col-md-6'>
                <FormLabel>Milestone *</FormLabel>
                <FormControl 
                  type="text" 
                  placeholder="Enter Milestone (e.g., Graduation, Marriage)"
                  value={formData.milestone}
                  onChange={(e) => handleFormDataChange('milestone', e.target.value)}
                  isInvalid={touched.milestone && !!formErrors.milestone}
                />
                {touched.milestone && formErrors.milestone && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.milestone}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group date-picker col-md-6'>
                <FormLabel>Estimated Date *</FormLabel>
                <div className="position-relative">
                  <DatePicker
                    ref={estimatedDatePickerRef}
                    value={startDate}
                    onChange={(dateObject: DateObject | null) => {
                      try {
                        if (dateObject) {
                          // Convert DateObject to JavaScript Date
                          const jsDate = dateObject.toDate();

                          if (giftType === 'Post-Mortem') {
                            // For Post-Mortem, create a date object with current year but selected month/day
                            const postMortemDate = new Date(new Date().getFullYear(), jsDate.getMonth(), jsDate.getDate());
                            setStartDate(postMortemDate);
                          } else {
                            setStartDate(jsDate);
                          }
                        } else {
                          setStartDate(null);
                        }

                        if (formErrors.estimatedDate) {
                          setFormErrors({ ...formErrors, estimatedDate: '' });
                        }
                      } catch (error) {

                        setStartDate(null);
                      }
                    }}
                    format={giftType === 'Post-Mortem' ? "MM/DD" : "MM/DD/YYYY"}
                    className={`form-control ${touched.estimatedDate && formErrors.estimatedDate ? 'is-invalid' : ''}`}
                    minDate={giftType === 'Post-Mortem' ? undefined : new Date()}
                    placeholder="Select an estimated date"
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ width: "100%", height: "48px", padding: "12px 45px 12px 16px" }}
                    onlyMonthPicker={giftType === 'Post-Mortem' ? false : undefined}
                    onlyYearPicker={false}
                    editable={false}
                    plugins={[]}
                  />
                  <img
                    src={calendarIcon}
                    alt="Calendar Icon"
                    className="calendar-icon"
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 2,
                      width: "20px",
                      height: "20px",
                      pointerEvents: "none"
                    }}
                  />
                </div>
                {touched.estimatedDate && formErrors.estimatedDate && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.estimatedDate}
                  </small>
                )}
              </FormGroup>
            </>
          )}

          {/* Hide Delivery Schedule for Conditional occasions */}
          {occasionType === "Fixed" && (
            <FormGroup className='form-group col-md-12 radio-buttons'>
              <span className='custom-label'>Delivery Schedule *</span>
              <div className='d-flex'>
                <FormGroup className='form-group'>
                  <Form.Check
                    type="radio"
                    label="One-Time"
                    name="delivery"
                    checked={deliverySchedule === "One-Time"}
                    onChange={() => {
                      setDeliverySchedule("One-Time");
                      setHasRadioChanges(true);
                    }}
                  />
                </FormGroup>
                <FormGroup className='form-group'>
                  <Form.Check
                    type="radio"
                    label="Recurring"
                    name="delivery"
                    checked={deliverySchedule === "Recurring"}
                    onChange={() => {
                      setDeliverySchedule("Recurring");
                      setHasRadioChanges(true);
                    }}
                  />
                </FormGroup>
              </div>
            </FormGroup>
          )}

          {/* Show Recurring info when selected */}
          {deliverySchedule === "Recurring" && (
            <div className="alert alert-info py-1" style={{ background: 'linear-gradient(135deg, rgb(248, 249, 255) 0%, rgb(231, 236, 241) 100%)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(57, 87, 160, 0.1)', boxShadow: 'rgba(57, 87, 160, 0.08) 0px 4px 20px', color: '#333333', fontStyle: 'italic' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiInfo className="my-auto" style={{ flexShrink: 0 }} />
                <small>
                  <strong>Recurring Delivery:</strong> Recurring gifts will be sent automatically at specified intervals.
                </small>
              </div>
            </div>
          )}

          {/* Conditionally render this section if "Fixed" + "Recurring" is selected */}
          {occasionType === "Fixed" && deliverySchedule === "Recurring" && (
            <>
              <FormGroup className='form-group col-md-6'>
                <FormLabel>Frequency *</FormLabel>
                <CustomSelect
                  value={formData.frequency}
                  onChange={(value) => handleFormDataChange('frequency', value)}
                  options={[
                    { value: 'Annually', label: 'Annually' },
                    { value: 'Monthly', label: 'Monthly' },
                    // { value: 'Weekly', label: 'Weekly' }
                  ]}
                  placeholder="Select Frequency"
                  isInvalid={touched.frequency && !!formErrors.frequency}
                  className="frequency-select"
                />
                {touched.frequency && formErrors.frequency && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.frequency}
                  </small>
                )}
              </FormGroup>

              {/* Updated: Removed End Date field and added No. of Years field for both Regular and Post-Mortem */}
              <FormGroup className='form-group col-md-6'>
                <FormLabel>No. of Years *</FormLabel>
                <FormControl 
                  type="text" 
                  placeholder="Enter number of years"
                  value={formData.numberOfYears}
                  onChange={(e) => handleFormDataChange('numberOfYears', e.target.value)}
                  isInvalid={touched.numberOfYears && !!formErrors.numberOfYears}
                />
                {touched.numberOfYears && formErrors.numberOfYears && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {formErrors.numberOfYears}
                  </small>
                )}
              </FormGroup>
            </>
          )}

          <FormGroup className='form-group col-md-12'>
            <FormLabel>Additional Comments</FormLabel>
            <FormControl 
              as="textarea" 
              rows={3} 
              placeholder="Any special delivery instructions or comments..."
              value={formData.additionalComments}
              onChange={(e) => handleFormDataChange('additionalComments', e.target.value)}
            />
          </FormGroup>

          <div className='d-flex justify-content-end action-buttons mt-4'>
            
              <motion.div {...subtleButtonHover} className='my-auto'>
                <Button 
                  className='draft-button w-auto me-3'
                  onClick={handleSaveAsDraft}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save as Draft'}
                </Button>
              </motion.div>
              <motion.div {...buttonHover}>
                <Button 
                  className='main-bttn btn-next w-auto'
                  onClick={handleCreateGift}
                  disabled={isLoading}
                  style={{ border: 'none' }}
                >
                  {isLoading ? (currentGift?._id ? 'Updating...' : 'Creating...') : 'Next'}
                </Button>
              </motion.div>
            
          </div>
        </div>
      </Form>

      {/* Reusable Modal for Add/Edit Recipient */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className='main-modal' backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? "Edit Recipient" : "Add Recipient"}

          <span style={{
              fontSize: '16px',
              color: '#000',
              margin: 0,
              lineHeight: '1.6',
              fontWeight: '500',
              width: '100%',
              display: 'block',
            }}>
              This information ensures your gift and audio card reach the right person at the right time.
            </span>

          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* <div style={{
            background: '#EEF4FB',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '25px',
            border: '1px solid #D4E4F7'
          }}>
            
          </div> */}
          <Form>
            <div className='row'>
              <FormGroup className='form-group col-md-6'>
                <FormLabel>Name *</FormLabel>
                <FormControl
                  name="name"
                  type="text"
                  value={recipientData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Name"
                  isInvalid={recipientTouched.name && !!recipientErrors.name}
                />
                {recipientTouched.name && recipientErrors.name && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.name}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Relationship *</FormLabel>
                <FormControl
                  name="relationship"
                  type="text"
                  value={recipientData.relationship}
                  onChange={handleInputChange}
                  placeholder="Enter Relationship"
                  isInvalid={recipientTouched.relationship && !!recipientErrors.relationship}
                />
                {recipientTouched.relationship && recipientErrors.relationship && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.relationship}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Address 1 *</FormLabel>
                <FormControl
                  name="address1"
                  type="text"
                  value={recipientData.address1}
                  onChange={handleInputChange}
                  placeholder="Enter Address 1 (e.g., 123 Main Street)"
                  autoComplete="address-line1"
                  spellCheck={false}
                  isInvalid={recipientTouched.address1 && !!recipientErrors.address1}
                />
                {recipientTouched.address1 && recipientErrors.address1 && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.address1}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Address 2</FormLabel>
                <FormControl
                  name="address2"
                  type="text"
                  value={recipientData.address2}
                  onChange={handleInputChange}
                  placeholder="Enter Address 2 (e.g., Apt 4B, Suite 100)"
                  autoComplete="address-line2"
                  spellCheck={false}
                />
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>State *</FormLabel>
                <SearchableSelect
                  value={recipientData.state}
                  onChange={(value) => {
                    // Clear error first, then update the value
                    if (value) {
                      setRecipientErrors(prev => ({ ...prev, state: undefined }));
                    }
                    const event = { target: { name: 'state', value } };
                    handleStateChange(event as any);
                  }}
                  options={states.map(state => ({
                    value: state._id,
                    label: state.name
                  }))}
                  placeholder="Select State"
                  searchPlaceholder="Search states..."
                  loading={loadingStates}
                  loadingText="Loading states..."
                  disabled={loadingStates}
                  isInvalid={recipientTouched.state && !!recipientErrors.state}
                  className="state-select"
                  clearable={true}
                />
                {recipientTouched.state && recipientErrors.state && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.state}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>City *</FormLabel>
                <SearchableSelect
                  value={recipientData.city}
                  onChange={(value) => {
                    // Clear error first, then update the value
                    if (value) {
                      setRecipientErrors(prev => ({ ...prev, city: undefined }));
                    }
                    handleInputChange({ target: { name: 'city', value } } as any);
                  }}
                  options={cities.map(city => ({
                    value: city._id,
                    label: city.name
                  }))}
                  placeholder={
                    !recipientData.state 
                      ? 'Select state first' 
                      : loadingCities 
                      ? 'Loading cities...' 
                      : 'Select City'
                  }
                  searchPlaceholder="Search cities..."
                  loading={loadingCities}
                  loadingText="Loading cities..."
                  disabled={loadingCities || !recipientData.state}
                  isInvalid={recipientTouched.city && !!recipientErrors.city}
                  className="city-select"
                  clearable={true}
                  noOptionsText={!recipientData.state ? 'Select state first' : 'No cities found'}
                />
                {recipientTouched.city && recipientErrors.city && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.city}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>ZIP *</FormLabel>
                <FormControl
                  name="zip"
                  type="text"
                  value={recipientData.zip}
                  onChange={handleInputChange}
                  placeholder="Enter ZIP"
                  maxLength={10}
                  isInvalid={recipientTouched.zip && !!recipientErrors.zip}
                />
                {recipientTouched.zip && recipientErrors.zip && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.zip}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Email *</FormLabel>
                <FormControl
                  name="email"
                  type="email"
                  value={recipientData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                  isInvalid={recipientTouched.email && !!recipientErrors.email}
                />
                {recipientTouched.email && recipientErrors.email && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.email}
                  </small>
                )}
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Phone *</FormLabel>
                <FormControl
                  name="phone"
                  type="tel"
                  value={recipientData.phone}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder="Enter Phone Number"
                  isInvalid={recipientTouched.phone && !!recipientErrors.phone}
                />
                {recipientTouched.phone && recipientErrors.phone && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.phone}
                  </small>
                )}
                
              </FormGroup>

              <FormGroup className='form-group col-md-6'>
                <FormLabel>Alternate Phone (Optional)</FormLabel>
                <FormControl
                  name="altPhone"
                  type="tel"
                  value={recipientData.altPhone}
                  onChange={handleAltPhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder="Enter Alternate Phone"
                  isInvalid={recipientTouched.altPhone && !!recipientErrors.altPhone}
                />
                {recipientTouched.altPhone && recipientErrors.altPhone && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontSize: '0.875rem' }}>
                    {recipientErrors.altPhone}
                  </small>
                )}
               
              </FormGroup>

              <div className='text-center mt-4'>
                <Button 
                  className="main-bttn" 
                  onClick={handleModalSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : (isEditMode ? "Update" : "Add")}
                </Button>
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default GiftInformationForm;
